/**
 * Agents (REQ-022).
 *
 * The document this implements takes one position, and so does this page:
 *
 * > An agent proposes. A person disposes.
 *
 * So the first thing on the screen is not a list of agents — it is the stop,
 * and then the queue of things waiting for a human. An agent with nobody
 * watching is not leverage; it is an unsupervised employee with the company's
 * mail server and customer database.
 *
 * Nothing here can be *made* to happen from this page that the server would
 * not also enforce. Whether a particular person may approve a particular
 * proposal is decided in the service against the agent's approver role, so
 * hiding a button is a convenience and never the control.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Loader2,
  Ban,
  Play,
} from "lucide-react";
import { useState } from "react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";

interface Budget {
  spentTodayCents: number;
  spentThisMonthCents: number;
  dailyBudgetCents: number;
  monthlyBudgetCents: number;
  exhausted: boolean;
}

interface Agent {
  key: string;
  name: string;
  purpose: string;
  owner: string;
  model: string;
  promptVersion: string;
  tools: string[];
  approverRole: string;
  enabled: boolean;
  version: number;
  lastReviewedAt?: string;
  budget: Budget;
}

interface Stop {
  stopped: boolean;
  at?: string;
  by?: string;
  reason?: string;
}

interface Proposal {
  id: string;
  agentKey: string;
  read: string[];
  action: string;
  confidence: number;
  createdAt: string;
}

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function AgentsPage() {
  const fmt = useLocaleFormat();
  const queryClient = useQueryClient();
  const [refusal, setRefusal] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);
  const [rejecting, setRejecting] = useState<string | null>(null);

  const said = (error: unknown, fallback: string) =>
    (error as { response?: { data?: { error?: { message?: string } } } })
      ?.response?.data?.error?.message ?? fallback;

  const agents = useQuery({
    queryKey: ["agents"],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { agents: Agent[]; stop: Stop };
      }>("/staff/agents");
      return response.data.data;
    },
  });

  const proposals = useQuery({
    queryKey: ["agents", "proposals"],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { proposals: Proposal[] };
      }>("/staff/agents/proposals");
      return response.data.data.proposals;
    },
    refetchInterval: 30_000,
  });

  const stop = useMutation({
    mutationFn: async (reason: string) => {
      await apiClient.post("/staff/agents/stop", { reason });
    },
    onSuccess: () => {
      setStopping(false);
      setRefusal(null);
      void queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (error) => setRefusal(said(error, "Could not stop the agents.")),
  });

  const release = useMutation({
    mutationFn: async () => {
      await apiClient.post("/staff/agents/release", {});
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["agents"] }),
    onError: (error) => setRefusal(said(error, "Could not release.")),
  });

  const toggle = useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) => {
      await apiClient.patch(`/staff/agents/${key}`, { enabled });
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["agents"] }),
    onError: (error) => setRefusal(said(error, "Could not change the agent.")),
  });

  const decide = useMutation({
    mutationFn: async (input: {
      id: string;
      decision: "approved" | "rejected";
      reason?: string;
    }) => {
      await apiClient.post(`/staff/agents/proposals/${input.id}/decide`, {
        decision: input.decision,
        reason: input.reason,
      });
    },
    onSuccess: () => {
      setRejecting(null);
      setRefusal(null);
      void queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (error) =>
      setRefusal(said(error, "Could not record the decision.")),
  });

  const stopped = agents.data?.stop.stopped;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Bot className="h-5 w-5 text-sky-600" /> Agents
        </h1>
        {stopped ? (
          <button
            onClick={() => release.mutate()}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700"
          >
            Release the stop
          </button>
        ) : (
          <button
            onClick={() => setStopping(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            <Ban className="h-4 w-4" /> Stop all agents
          </button>
        )}
      </div>

      {/* The stop, first. This page is a supervision surface before it is a
          management one. */}
      {stopped && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
          <Ban className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Every agent is stopped.</p>
            <p className="mt-1">
              {agents.data?.stop.by} stopped them
              {agents.data?.stop.at &&
                ` on ${fmt.dateTime(agents.data.stop.at)}`}
              : {agents.data?.stop.reason}. Runs in flight were abandoned.
            </p>
          </div>
        </div>
      )}

      {stopping && (
        <form
          className="flex flex-wrap items-end gap-2 rounded-lg border border-red-200 bg-white p-4"
          onSubmit={(event) => {
            event.preventDefault();
            const reason = new FormData(event.currentTarget).get("reason");
            stop.mutate(String(reason ?? ""));
          }}
        >
          <label className="flex-1 text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Why are you stopping them?
            </span>
            <input
              name="reason"
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <span className="mt-1 block text-xs text-gray-500">
              A stop nobody can explain is a stop nobody dares release.
            </span>
          </label>
          <button
            type="submit"
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white"
          >
            Stop everything
          </button>
          <button
            type="button"
            onClick={() => setStopping(false)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
        </form>
      )}

      {refusal && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{refusal}</p>
        </div>
      )}

      {/* Waiting for a person. Above the agents themselves, because this is
          the work; the agent list is configuration. */}
      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <h2 className="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-900">
          Waiting for a person
        </h2>
        {proposals.isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !proposals.data?.length ? (
          <p className="p-6 text-sm text-gray-500">
            Nothing is waiting. Agents propose; nothing is applied without
            somebody here deciding.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {proposals.data.map((proposal) => (
              <li key={proposal.id} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-900">
                      {proposal.action}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {proposal.agentKey} · confidence{" "}
                      {Math.round(proposal.confidence * 100)}% · read:{" "}
                      {proposal.read.join(", ") || "nothing recorded"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        decide.mutate({
                          id: proposal.id,
                          decision: "approved",
                        })
                      }
                      className="rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => setRejecting(proposal.id)}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700"
                    >
                      Reject
                    </button>
                  </div>
                </div>
                {rejecting === proposal.id && (
                  <form
                    className="mt-3 flex flex-wrap items-end gap-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const reason = new FormData(event.currentTarget).get(
                        "reason",
                      );
                      decide.mutate({
                        id: proposal.id,
                        decision: "rejected",
                        reason: String(reason ?? ""),
                      });
                    }}
                  >
                    <label className="flex-1 text-sm">
                      <span className="mb-1 block text-gray-600">
                        Why? This becomes one of the agent's test cases.
                      </span>
                      <input
                        name="reason"
                        required
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                    </label>
                    <button
                      type="submit"
                      className="rounded-lg bg-gray-800 px-3 py-2 text-sm text-white"
                    >
                      Reject
                    </button>
                  </form>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <h2 className="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-900">
          Declared agents
        </h2>
        {agents.isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !agents.data?.agents.length ? (
          <p className="p-6 text-sm text-gray-500">
            No agent is declared, so none can run. An undeclared agent cannot
            execute — that is the point of the registry, not a gap in it.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Agent</th>
                  <th className="px-4 py-2 font-medium">Owner</th>
                  <th className="px-4 py-2 font-medium">Spend today</th>
                  <th className="px-4 py-2 font-medium">State</th>
                  <th className="px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {agents.data.agents.map((agent) => (
                  <tr key={agent.key} className="align-top">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">
                        {agent.name}
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500">
                        {agent.model} · prompt {agent.promptVersion} · v
                        {agent.version} · approved by {agent.approverRole}
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-400">
                        {agent.tools.length} tools
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{agent.owner}</td>
                    <td className="px-4 py-3 tabular-nums text-gray-600">
                      {money(agent.budget.spentTodayCents)} /{" "}
                      {money(agent.budget.dailyBudgetCents)}
                    </td>
                    <td className="px-4 py-3">
                      {agent.budget.exhausted ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
                          <AlertTriangle className="h-3 w-3" /> budget reached
                        </span>
                      ) : agent.enabled ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" /> enabled
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">disabled</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() =>
                          toggle.mutate({
                            key: agent.key,
                            enabled: !agent.enabled,
                          })
                        }
                        disabled={stopped}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                      >
                        <Play className="h-3.5 w-3.5" />
                        {agent.enabled ? "Disable" : "Enable"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
